/**
 * closingAgent.js
 * Custom React hooks and helpers for the Closing Flow feature.
 * Provides data fetching and mutations for closing checklists and progress.
 */

import { useState, useEffect, useCallback } from 'react';
import APIAgent from './apiAgent';

// ---------------------------------------------------------------------------
// useClosingChecklist — load closing checklist items for a transaction
// ---------------------------------------------------------------------------
export function useClosingChecklist(transactionId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/closing/${transactionId}/checklist`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load closing checklist');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  return { items, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// useClosingProgress — load closing progress summary for a transaction
// ---------------------------------------------------------------------------
export function useClosingProgress(transactionId) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(`/api/closing/${transactionId}/progress`);
      setProgress(data);
    } catch (err) {
      setError(err.message || 'Failed to load closing progress');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => { load(); }, [load]);

  return { progress, loading, error, reload: load };
}

// ---------------------------------------------------------------------------
// updateClosingItem — update a single closing checklist item
// ---------------------------------------------------------------------------
export async function updateClosingItem(itemId, data) {
  return APIAgent.request(`/api/closing/items/${itemId}`, 'PUT', data);
}
